// Única fuente de verdad de los nombres de módulo para eventos_v2.
//
// La normalización se aplica centralmente al escribir (lib/eventTracking.js →
// trackEvent), de modo que ningún componente pueda dejar un nombre inconsistente
// en la BD, pase lo que pase en el call-site. Los lectores de eventos_v2.modulo
// (milestones, re-engagement) usan moduloCanonico() para trabajar con el mismo
// vocabulario, ya sea una fila histórica (nombre viejo) o nueva (canónico).
//
// Convención: snake_case. "MiPAE en turno" es un módulo paraguas: cada submódulo
// conserva su identidad con el prefijo `mi_turno_` para que quede claro que vive
// dentro de MiPAE en turno sin perder el detalle.
//
// Este archivo NO importa nada (constantes + funciones puras) para poder usarse
// tanto en el bundle del cliente como en funciones serverless de api/.

export const MODULOS = {
  HOME:              'home',
  AUTH:              'auth',
  PERFIL:            'perfil',
  ONBOARDING:        'onboarding',
  ONBOARDING_AGENTE: 'onboarding_agente',
  PAE_RAPIDO:        'pae_rapido',
  PAE_DETALLADO:     'pae_detallado',
  REVISA:            'revisa',
  BITACORA:          'bitacora',
  CALENDARIO:        'calendario',
  ESCALAS:           'escalas',
  // La Enciclopedia es pestaña central de la barra pero no emitía eventos: la
  // auditoría 2026-07-06 la encontró invisible en eventos_v2. Canónico nuevo +
  // alias del nombre interno del componente (appState "referencia").
  ENCICLOPEDIA:      'enciclopedia',
  // Guía de medicamentos (fichas de fármacos). Misma historia que la
  // Enciclopedia: la auditoría 2026-08-01 encontró el módulo sin UNA fila en
  // eventos_v2 — el appState "fichas" nunca entró al MODULO_MAP del router ni
  // el componente emitió lo suyo. El módulo emite sus propios eventos.
  FICHAS:            'fichas',
  GES:               'ges',
  RESPIRA:           'respira',
  // Bienestar (2026-09-23): home propio que reúne Respira, las pausas guiadas
  // y la Bitácora. Antes la pestaña era un popover sin evento alguno, así que
  // nadie sabía cuántas personas llegaban (plan-bienestar-modulo §1).
  BIENESTAR:         'bienestar',
  COMPANERO_GLOBAL:  'companero_global',
  LANZAMIENTO_PLUS:  'lanzamiento_plus',
  BIBLIOGRAFIA:      'bibliografia',
  RETO:              'reto',            // Reto del día (RetoDelDia.jsx)
  PREPARA_PRACTICA:  'prepara_practica',
  PREPARA_INTERNADO: 'prepara_internado',
  PREPARA_EXAMEN:    'prepara_examen',
  TU_CAMINO:         'tu_camino',
  OPINION:           'opinion',          // "Danos tu opinión" (canal FENEECh)
  JEFATURA:          'jefatura',         // tablero de la jefatura (/jefatura)
  // ── MiPAE en turno (módulo paraguas con submódulos) ──
  MI_TURNO:           'mi_turno',            // pantalla de registro del turno
  // El dictado es TRANSVERSAL (turno, Benito, bitácora, examen) y el hook que
  // lo implementa no sabe en qué pantalla vive. Sus fallas se emiten bajo este
  // módulo propio para no inventarles una pantalla (2026-09-03, iPhone).
  DICTADO:            'dictado',
  TENS:               'tens',                // capa TENS (SPEC 3, v1)
  MI_TURNO_ENTREGA:   'mi_turno_entrega',
  MI_TURNO_EVOLUCION: 'mi_turno_evolucion',
  MI_TURNO_PREPARA:   'mi_turno_prepara',
};

// Alias históricos/inconsistentes → canónico. Se aplican al escribir y al leer,
// para unificar datos viejos y nuevos bajo el mismo nombre. Exportado para que
// el test de call-sites (modulos.test.js) pueda escanear el código en busca de
// alias reintroducidos sin mantener una lista paralela.
export const ALIAS = {
  'registro-turno':    MODULOS.MI_TURNO,
  'registro_turno':    MODULOS.MI_TURNO,
  'entrega-turno':     MODULOS.MI_TURNO_ENTREGA,
  'evolucion-sistema': MODULOS.MI_TURNO_EVOLUCION,
  'prepara_turno':     MODULOS.MI_TURNO_PREPARA,
  'companero-global':  MODULOS.COMPANERO_GLOBAL,
  'referencia':        MODULOS.ENCICLOPEDIA,
};

// Submódulo (etiqueta corta) de cada pieza de MiPAE en turno, para desgloses.
export const SUBMODULO_MI_TURNO = {
  [MODULOS.MI_TURNO]:           'registro',
  [MODULOS.MI_TURNO_ENTREGA]:   'entrega',
  [MODULOS.MI_TURNO_EVOLUCION]: 'evolucion',
  [MODULOS.MI_TURNO_PREPARA]:   'prepara',
};

// Normaliza un nombre de módulo a su forma canónica. Idempotente: un nombre ya
// canónico se devuelve intacto.
export function moduloCanonico(modulo) {
  if (!modulo) return modulo;
  return ALIAS[modulo] || modulo;
}

// ¿El módulo (crudo o canónico) pertenece al paraguas MiPAE en turno?
export function esMiTurno(modulo) {
  const c = moduloCanonico(modulo);
  return c === MODULOS.MI_TURNO || (typeof c === 'string' && c.startsWith('mi_turno_'));
}
